import { Button, Input, Select } from "@softmax/design-system";
import * as stylex from "@stylexjs/stylex";
import { useState } from "react";
import {
  kindOptions,
  type EditorLink,
  type EditorLinkKind,
} from "./link-records";
import { linkStyles as styles } from "./links.stylex";

type NewLinkFormProps = Readonly<{
  loading: boolean;
  maxLabelLength: number;
  maxLinks: number;
  linkCount: number;
  onAdd(link: EditorLink): string | null;
}>;

export function NewLinkForm({
  loading,
  maxLabelLength,
  maxLinks,
  linkCount,
  onAdd,
}: NewLinkFormProps) {
  const [newLabel, setNewLabel] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [newKind, setNewKind] = useState<EditorLinkKind | "">("");
  const [addError, setAddError] = useState<string | null>(null);

  function addLink() {
    if (loading) return;

    const error = onAdd({
      label: newLabel.trim(),
      url: newUrl,
      kind: newKind || null,
    });
    setAddError(error);

    if (error) return;

    setNewLabel("");
    setNewUrl("");
    setNewKind("");
  }

  if (linkCount >= maxLinks) {
    return (
      <span {...stylex.props(styles.label)}>
        {maxLinks}/{maxLinks} links
      </span>
    );
  }

  return (
    <div {...stylex.props(styles.card)}>
      <Input
        value={newLabel}
        onChange={(event) => setNewLabel(event.target.value)}
        placeholder="Label (e.g. Discord)"
        aria-label="New link label"
        maxLength={maxLabelLength}
        disabled={loading}
      />
      <Input
        type="url"
        value={newUrl}
        onChange={(event) => setNewUrl(event.target.value)}
        placeholder="https://…"
        aria-label="New link URL"
        disabled={loading}
      />
      <Select<EditorLinkKind | "">
        value={newKind}
        options={kindOptions}
        onValueChange={setNewKind}
        aria-label="New link kind"
        disabled={loading}
      />
      {addError ? (
        <span role="alert" {...stylex.props(styles.error)}>
          {addError}
        </span>
      ) : null}
      <Button size="sm" disabled={loading} onClick={addLink}>
        Add link
      </Button>
    </div>
  );
}
